import {
  CircularProgress,
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
} from "@mui/material";
import { ReactNode, forwardRef } from "react";
import { TYPOGRAPHY_SCALE } from "../../constants/styles/typography";
import { COLOR_CONSTANTS } from "../../constants/styles";

export type ButtonIntent = "primary" | "error" | "inherit" | "inheritWhite";
export type ButtonEmphasis = "contained" | "outlined" | "text";
export type ButtonSize = "small" | "medium" | "large";

/**
 * 디자인 시스템 버튼 props — MUI `Button` 의 color/variant/size 를 자체 토큰으로 교체.
 *
 * 크기 토큰별 높이: small=28px, medium=36px, large=44px.
 * `loading=true` 시 자동으로 disabled 처리되고 "Loading" 텍스트와 스피너로 교체된다.
 *
 * @example
 * ```tsx
 * <Button onClick={handleSave}>저장</Button>
 * <Button emphasis="outlined" intent="primary">취소</Button>
 * <Button intent="error" onClick={handleDelete}>삭제</Button>
 * <Button loading={isSubmitting}>제출</Button>
 * <Button emphasis="text" intent="inherit">건너뛰기</Button>
 * ```
 */
export interface ButtonProps
  extends Omit<MuiButtonProps, "color" | "variant" | "size" | "startIcon"> {
  /** 의도 색상 토큰 (기본 `"primary"`) */
  intent?: ButtonIntent;
  /** 강조 토큰 — contained/outlined/text (기본 `"contained"`) */
  emphasis?: ButtonEmphasis;
  /** 크기 토큰 (기본 `"medium"`) */
  size?: ButtonSize;
  /** true 시 스피너 + "Loading" 텍스트로 교체, 자동 disabled */
  loading?: boolean;
  disabled?: boolean;
  /** 라벨 왼쪽에 끼울 아이콘 */
  startIcon?: ReactNode;
  /**
   * 테스트 앵커. JSX 직접 사용은 TS 의 하이픈 속성 규칙으로 통과하지만,
   * `ModalBottom` 의 `rightButtonProps` 처럼 **객체 리터럴**로 전달할 때 타입에 필요하다.
   * 값 규약은 `src/test/testIds.ts` 참조.
   */
  "data-testid"?: string;
}

const ButtonStyles = {
  emphasisToVariant: {
    contained: "contained",
    outlined: "outlined",
    text: "text",
  } as const,
  spinnerSizes: {
    small: 16,
    medium: 18,
    large: 20,
  },
  sizes: {
    small: {
      height: 28,
      minHeight: 28,
      paddingX: "12px",
      paddingY: 0,
    },
    medium: {
      height: 36,
      minHeight: 36,
      paddingX: "16px",
      paddingY: 0,
    },
    large: {
      height: 44,
      minHeight: 44,
      paddingX: "20px",
      paddingY: 0,
    },
  },
  typography: {
    small: TYPOGRAPHY_SCALE.buttonSmall,
    medium: TYPOGRAPHY_SCALE.buttonMedium,
    large: TYPOGRAPHY_SCALE.buttonLarge,
  },
  colors: {
    contained: {
      primary: {
        backgroundColor: COLOR_CONSTANTS.primary.main,
        color: COLOR_CONSTANTS.primary.contrastText,
        "&:hover": { backgroundColor: COLOR_CONSTANTS.primary.dark },
        "&:active": { backgroundColor: COLOR_CONSTANTS.primary.main },
      },
      error: {
        backgroundColor: COLOR_CONSTANTS.error.main,
        color: COLOR_CONSTANTS.primary.contrastText,
        "&:hover": { backgroundColor: COLOR_CONSTANTS.error.dark },
        "&:active": { backgroundColor: COLOR_CONSTANTS.error.main },
      },
      inherit: {
        backgroundColor: COLOR_CONSTANTS.COLOR.GREY[300],
        color: COLOR_CONSTANTS.text.secondary,
        "&:hover": { backgroundColor: COLOR_CONSTANTS.action.hover },
        "&:active": { backgroundColor: COLOR_CONSTANTS.COLOR.GREY[300] },
      },
      inheritWhite: {
        backgroundColor: COLOR_CONSTANTS.common.white_states.main,
        color: COLOR_CONSTANTS.primary.contrastText,
        "&:hover": { backgroundColor: COLOR_CONSTANTS.COLOR.GREY[100] },
        "&:active": { backgroundColor: COLOR_CONSTANTS.COLOR.GREY[300] },
      },
    },
    outlined: {
      primary: {
        color: COLOR_CONSTANTS.primary.main,
        borderColor: COLOR_CONSTANTS.primary._states.outlinedBorder,
        "&:hover": { backgroundColor: COLOR_CONSTANTS.primary._states.hover },
      },
      error: {
        color: COLOR_CONSTANTS.error.main,
        borderColor: COLOR_CONSTANTS.error._states.outlinedBorder,
        "&:hover": { backgroundColor: COLOR_CONSTANTS.error._states.hover },
      },
      inheritWhite: {
        color: COLOR_CONSTANTS.common.white_states.main,
        borderColor: COLOR_CONSTANTS.common.white_states.main,
        "&:hover": { backgroundColor: COLOR_CONSTANTS.action.hover },
      },
      inherit: {
        color: COLOR_CONSTANTS.text.secondary,
        borderColor: COLOR_CONSTANTS._components.input.outlined.enabledBorder,
        "&:hover": { backgroundColor: COLOR_CONSTANTS.action.hover },
      },
    },
    text: {
      primary: {
        color: COLOR_CONSTANTS.primary.main,
        "&:hover": { backgroundColor: COLOR_CONSTANTS.primary._states.hover },
      },
      error: {
        color: COLOR_CONSTANTS.error.main,
        "&:hover": { backgroundColor: COLOR_CONSTANTS.error._states.hover },
      },
      inheritWhite: {
        color: COLOR_CONSTANTS.common.white_states.main,
        "&:hover": { backgroundColor: COLOR_CONSTANTS.action.hover },
      },
      inherit: {
        color: COLOR_CONSTANTS.text.secondary,
      },
    },
  },
  disabled: {
    contained: {
      "&.Mui-disabled": {
        backgroundColor: COLOR_CONSTANTS.COLOR.GREY[300],
        color: COLOR_CONSTANTS.text.disabled,
      },
    },
    outlined: {
      "&.Mui-disabled": {
        color: COLOR_CONSTANTS.text.disabled,
        borderColor: COLOR_CONSTANTS.divider.divider,
      },
    },
    text: {
      "&.Mui-disabled": {
        color: COLOR_CONSTANTS.text.disabled,
      },
    },
  },
} as const;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      intent = "primary",
      emphasis = "contained",
      size = "medium",
      loading = false,
      disabled = false,
      startIcon,
      children,
      sx,
      ...muiProps
    },
    ref
  ) => {
    const rootColorSx = ButtonStyles.colors[emphasis][intent];

    const sizeSx = ButtonStyles.sizes[size];
    const isDisabled = disabled || loading;
    const disabledSx = isDisabled ? ButtonStyles.disabled[emphasis] : {};
    const labelTypographySx = ButtonStyles.typography[size];
    const loadingIcon = loading ? (
      <CircularProgress
        size={ButtonStyles.spinnerSizes[size]}
        color="inherit"
        className="dsq-Button__spinner"
      />
    ) : undefined;

    const startIconToRender = loading ? loadingIcon : startIcon;

    const ChildrenToRender = loading ? "Loading" : children;

    return (
      <MuiButton
        ref={ref}
        sx={{
          ...sizeSx,
          ...rootColorSx,
          ...disabledSx,
          ...labelTypographySx,
          ...sx,
        }}
        variant={ButtonStyles.emphasisToVariant[emphasis]}
        startIcon={startIconToRender}
        disabled={isDisabled}
        disableElevation
        aria-busy={loading || undefined}
        aria-live={loading ? "polite" : undefined}
        {...muiProps}
      >
        {ChildrenToRender}
      </MuiButton>
    );
  }
);

Button.displayName = "Button";
